<!--
  Generic integration workflow for any service that maps an external data
  source to Routine tasks. Covers Gmail, Todoist, Asana, and future additions.

  Connecting an account and setting up task sync are decoupled:
    - "Connect" runs OAuth and lands directly on the ready screen.
    - Each connected account has its own independent "Set up task sync" action.
    - Disconnecting an account automatically removes its task sync.

  mainPhase:   "empty" | "ready"
  wizardPhase: "connecting" | "configure" | "mapping" | "creating" | "failed" | "success"
               | null
-->
<template>
  <div class="settings-page integration">
    <div class="title">
      <div>
        <img :src="app.logoUri" class="header-icon" />
        <h2>{{ app.name }}</h2>
        <button
          class="copy-btn"
          v-tippy="{ content: 'Copy id', arrow: false, placement: 'top' }"
          @click="copyIntegrationId()"
        >
          <CopyIcon />
        </button>
      </div>
    </div>

    <p class="description">
      {{ config ? config.description : app.description }}
      <span
        v-if="config && app.description"
        class="app-description-hint"
        v-tippy="{ content: app.description, arrow: false, placement: 'right' }"
      >
        <InfoIcon />
      </span>
    </p>

    <!-- Empty: no account connected yet -->
    <section v-if="mainPhase === 'empty'" class="workflow-empty">
      <div class="workflow-empty-card">
        <div class="workflow-empty-logo-wrap">
          <img :src="app.logoUri" class="workflow-empty-logo" />
        </div>
        <div class="workflow-empty-body">
          <p class="workflow-empty-headline">
            No {{ config?.label ?? app.name }} account connected
          </p>
          <p class="workflow-empty-sub">
            Connect your account to start syncing into Routine.
          </p>
        </div>
        <UpgradeButton
          :entitlement-key="ENTITLEMENT_KEY"
          :organization-id="orgId"
          :entitled="canAddConnection"
          kind="main"
          type="primary"
          :text="`Connect ${config?.label ?? app.name}`"
          @click="addAccount()"
        />
      </div>
    </section>

    <!-- Ready: one row per connected account -->
    <section v-else-if="mainPhase === 'ready'" class="workflow-ready">
      <h3>Accounts</h3>
      <div v-for="conn in connections" :key="conn.id" class="workflow-account">
        <div class="workflow-ready-row">
          <div class="workflow-ready-text">
            <img :src="app.logoUri" class="workflow-ready-icon" />
            <div class="workflow-ready-name">
              {{
                connectionLabels[conn.id] ??
                getConnectionDetail(conn) ??
                conn.name
              }}
            </div>
            <span
              class="workflow-ready-status-dot"
              :class="{
                healthy: getConnectionHealth(conn).healthy,
                error: !getConnectionHealth(conn).healthy,
              }"
              v-tippy="{
                content: getConnectionHealth(conn).tooltip,
                arrow: false,
                placement: 'top',
              }"
            />
          </div>

          <div class="workflow-ready-actions">
            <ButtonMenu :offset="[0, 4]" placement="bottom-end">
              <template #btn>
                <BaseButton kind="auxiliary">
                  <template #icon><MoreIcon /></template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  class="context-action"
                  kind="auxiliary"
                  text="Reconnect"
                  is-expanded
                  @click="reconnectConnection(conn.id)"
                >
                  <template #icon><RefreshIcon /></template>
                </BaseButton>
                <BaseButton
                  class="context-action"
                  kind="auxiliary"
                  text="Disconnect"
                  tint="red"
                  is-expanded
                  @click="deleteConnection(conn.id)"
                >
                  <template #icon><DeleteIcon /></template>
                </BaseButton>
              </template>
            </ButtonMenu>
          </div>
        </div>

        <ConnectionVersionUpdate
          v-if="connectionVersionActionFor(conn) === 'update'"
          :updating="isUpdatingConnection(conn.id)"
          @update="updateConnection(conn.id)"
        />

        <!-- Empty state when the account has no task syncs and no data sources -->
        <div
          v-if="
            taskSyncsFor(conn.id).length === 0 &&
            dataSourcesFor(conn.id).length === 0
          "
          class="workflow-account-setup"
        >
          <!-- Task sync setup (config present) -->
          <BaseAlert v-if="config" state="info" class="workflow-setup-card">
            <div class="workflow-empty-body">
              <h4 class="workflow-empty-headline">
                Set up {{ config.label }} sync
              </h4>
              <p class="workflow-empty-sub">{{ config.description }}</p>
            </div>
            <div class="workflow-setup-actions">
              <BaseButton
                kind="main"
                type="primary"
                text="Set up integration"
                @click="openTaskSyncWizard(conn.id)"
              />
              <button
                v-if="dataSourcesSupported"
                class="workflow-add-source-link"
                @click="openAddDataCollection()"
              >
                or add data source
              </button>
            </div>
          </BaseAlert>

          <!-- No task sync, data sources available -->
          <BaseAlert
            v-else-if="dataSourcesSupported"
            state="info"
            class="workflow-setup-card"
          >
            <div class="workflow-empty-body">
              <h4 class="workflow-empty-headline">
                Setup {{ app.name }} integration
              </h4>
              <p class="workflow-empty-sub">
                {{
                  app.description ||
                  "Add a data source to create objects in Routine"
                }}
              </p>
            </div>
            <div class="workflow-setup-actions">
              <BaseButton
                kind="main"
                type="primary"
                text="Add data source"
                @click="openAddDataCollection()"
              />
            </div>
          </BaseAlert>

          <!-- No task sync, no data sources: AI agents only -->
          <IntegrationDataSourcesSection
            v-else
            :enabled-data-collections="[]"
            :connections="connections"
            :data-sources-supported="false"
            :on-add="openAddDataCollection"
            :on-delete-source="deleteSource"
            :on-force-refresh="forceRefreshSource"
            :on-open-database="openDatabase"
          />
        </div>

        <!-- Unified list: one block per task sync, then one per data source -->
        <div
          v-if="
            taskSyncsFor(conn.id).length > 0 ||
            dataSourcesFor(conn.id).length > 0
          "
          class="workflow-task-sync"
        >
          <div
            v-for="sync in taskSyncsFor(conn.id)"
            :key="sync.key"
            class="workflow-sync"
          >
            <div class="workflow-sync-row">
              <div class="workflow-sync-text">
                <TaskIcon size="s" class="workflow-sync-icon" />
                <div class="workflow-sync-name">{{ sync.name }}</div>
                <Spinner
                  v-if="sync.status === 'pending'"
                  class="workflow-sync-spinner"
                  v-tippy="{
                    content: 'Setting up…',
                    arrow: false,
                    placement: 'top',
                  }"
                />
                <span
                  v-else
                  class="workflow-ready-status-dot"
                  :class="{
                    healthy: sync.status === 'ready',
                    error: sync.status === 'error',
                  }"
                  v-tippy="{
                    content:
                      sync.status === 'ready'
                        ? sync.hint
                        : 'This sync has an error, contact Routine support if it persists.',
                    arrow: false,
                    placement: 'top',
                  }"
                />
                <span
                  v-if="needsTaskBindingRepair(sync)"
                  class="workflow-sync-binding-warning"
                >
                  Task binding not configured
                </span>
              </div>
              <div class="workflow-sync-actions">
                <span
                  v-if="hasCompletionSelect(sync)"
                  class="workflow-sync-completion-label"
                  v-tippy="{
                    content: 'When completing an imported task',
                    arrow: false,
                    placement: 'top',
                  }"
                  >On completion:</span
                >
                <BaseInputSelect
                  v-if="hasCompletionSelect(sync)"
                  select-only
                  class="workflow-completion-select"
                  :options="completionOptionsFor(sync)"
                  :value="completionValueFor(sync)"
                  @update:value="setCompletionAction(sync, Number($event))"
                />
                <BaseButton
                  v-if="needsTaskBindingRepair(sync)"
                  kind="auxiliary"
                  text="Set up task binding"
                  :state="isRepairingTaskBinding(sync) ? 'loading' : 'default'"
                  @click="repairTaskBinding(sync)"
                />
                <button
                  v-if="sync.status === 'ready'"
                  class="workflow-how-to-use"
                  @click="showHowToUse(conn.id, sync)"
                >
                  <InfoIcon class="workflow-how-to-use-icon" />
                </button>
                <ButtonMenu :offset="[0, 4]" placement="bottom-end">
                  <template #btn>
                    <BaseButton kind="auxiliary">
                      <template #icon><MoreIcon /></template>
                    </BaseButton>
                  </template>
                  <template #content>
                    <BaseButton
                      v-if="sync.status === 'ready'"
                      class="context-action"
                      kind="auxiliary"
                      text="Refresh"
                      is-expanded
                      @click="forceRefreshSync(sync)"
                    >
                      <template #icon><RefreshIcon /></template>
                    </BaseButton>
                    <BaseButton
                      v-if="canCustomizeSync(sync)"
                      class="context-action"
                      kind="auxiliary"
                      text="Customize"
                      is-expanded
                      @click="customizeSync(sync)"
                    >
                      <template #icon><EditIcon /></template>
                    </BaseButton>
                    <BaseButton
                      class="context-action"
                      kind="auxiliary"
                      text="Remove"
                      tint="red"
                      is-expanded
                      @click="removeSync(sync)"
                    >
                      <template #icon><DeleteIcon /></template>
                    </BaseButton>
                  </template>
                </ButtonMenu>
              </div>
            </div>
          </div>

          <!-- Data sources (non-task-sync collections) -->
          <div
            v-for="item in dataSourcesFor(conn.id)"
            :key="item.key"
            class="workflow-sync"
          >
            <div
              class="workflow-sync-row"
              :class="{ clickable: item.tableId !== null }"
              @click="openSourceDatabase(item, $event)"
            >
              <div class="workflow-sync-text">
                <DatabaseIcon class="workflow-sync-icon" />
                <div class="workflow-sync-name">{{ item.databaseName }}</div>
                <Spinner
                  v-if="item.status === 'pending'"
                  class="workflow-sync-spinner"
                  v-tippy="{
                    content: item.statusText,
                    arrow: false,
                    placement: 'top',
                  }"
                />
                <span
                  v-else
                  class="workflow-ready-status-dot"
                  :class="{
                    healthy: item.status === 'ready',
                    error: item.status === 'error',
                  }"
                  v-tippy="{
                    content: item.statusText,
                    arrow: false,
                    placement: 'top',
                  }"
                />
                <span
                  v-if="item.subStatus"
                  class="workflow-sub-status-icon"
                  v-tippy="{
                    content: item.subStatus.tooltip,
                    arrow: false,
                    placement: 'top',
                  }"
                >
                  <InfoIcon v-if="item.subStatus.icon === 'info'" />
                  <WarningIcon v-else />
                </span>
              </div>
              <div class="workflow-sync-actions" @click.stop>
                <ButtonMenu :offset="[0, 4]" placement="bottom-end">
                  <template #btn>
                    <BaseButton kind="auxiliary">
                      <template #icon><MoreIcon /></template>
                    </BaseButton>
                  </template>
                  <template #content>
                    <BaseButton
                      v-if="item.tableId !== null"
                      class="context-action"
                      kind="auxiliary"
                      text="See objects"
                      is-expanded
                      @click="openSourceDatabase(item, $event)"
                    >
                      <template #icon><DatabaseIcon /></template>
                    </BaseButton>
                    <BaseButton
                      v-if="item.dataCollection.status === 'ready'"
                      class="context-action"
                      kind="auxiliary"
                      text="Refresh"
                      is-expanded
                      @click="
                        forceRefreshSource(
                          item.workspaceId,
                          item.dataCollectionId,
                          item.dataCollection
                        )
                      "
                    >
                      <template #icon><RefreshIcon /></template>
                    </BaseButton>
                    <BaseButton
                      class="context-action"
                      kind="auxiliary"
                      text="Delete"
                      tint="red"
                      is-expanded
                      @click="
                        deleteSource(
                          item.workspaceId,
                          item.dataCollectionId,
                          item.dataCollection
                        )
                      "
                    >
                      <template #icon><DeleteIcon /></template>
                    </BaseButton>
                  </template>
                </ButtonMenu>
              </div>
            </div>
          </div>

          <div class="workflow-add-sync">
            <!-- Both task sync and data sources available: offer a choice -->
            <ButtonMenu
              v-if="config && dataSourcesSupported"
              :offset="[0, 4]"
              placement="bottom-start"
            >
              <template #btn>
                <BaseButton kind="main" type="secondary" text="Add">
                  <template #icon><AddIcon /></template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  class="context-action"
                  kind="auxiliary"
                  text="Task sync"
                  is-expanded
                  @click="openTaskSyncWizard(conn.id)"
                >
                  <template #icon><TaskIcon size="s" /></template>
                </BaseButton>
                <BaseButton
                  class="context-action"
                  kind="auxiliary"
                  text="Data source"
                  is-expanded
                  @click="openAddDataCollection()"
                >
                  <template #icon><DatabaseIcon /></template>
                </BaseButton>
              </template>
            </ButtonMenu>
            <BaseButton
              v-else-if="config"
              kind="main"
              type="secondary"
              text="Add"
              @click="openTaskSyncWizard(conn.id)"
            >
              <template #icon><AddIcon /></template>
            </BaseButton>
            <BaseButton
              v-else-if="dataSourcesSupported"
              kind="main"
              type="secondary"
              text="Add"
              @click="openAddDataCollection()"
            >
              <template #icon><AddIcon /></template>
            </BaseButton>
          </div>
        </div>
      </div>

      <UpgradeButton
        :entitlement-key="ENTITLEMENT_KEY"
        :organization-id="orgId"
        :entitled="canAddConnection"
        kind="main"
        type="secondary"
        text="Add account"
        @click="addAccount()"
      />
    </section>

    <!-- Wizard modal: OAuth connecting + task sync configure / creating / success -->
    <VueFinalModal
      v-model="customizeOpen"
      :focus-trap="modalFocusTrap"
      class="final-modal"
      :class="{
        'theme--default': theme === 'light',
        'theme--dark': theme === 'dark',
      }"
      overlay-transition="vfm-fade"
    >
      <TaskBindingEditor
        v-if="customizeBinding && customizeDatabase"
        :binding-id="customizeBinding.id"
        :binding="customizeBinding.binding"
        :database="customizeDatabase"
        :sections="customizeSections"
        :status-options="customizeStatusOptions"
        :status-column-label="customizeStatusColumnLabel"
        :scheduled-column-label="customizeScheduledColumnLabel"
        :can-reset="customizeSyncEntry !== null"
        :resetting="
          customizeSyncEntry !== null &&
          isRepairingTaskBinding(customizeSyncEntry)
        "
        @close="customizeOpen = false"
        @reset="resetCustomizedBinding"
      />
    </VueFinalModal>

    <VueFinalModal
      v-model="wizardOpen"
      :focus-trap="modalFocusTrap"
      class="final-modal"
      :class="{
        'theme--default': theme === 'light',
        'theme--dark': theme === 'dark',
      }"
      :click-to-close="wizardPhase !== 'creating'"
      :esc-to-close="wizardPhase !== 'creating'"
      overlay-transition="vfm-fade"
      @closed="onWizardClosed"
    >
      <div class="content workflow-wizard-content">
        <div class="header">
          <div class="title">
            <img :src="app.logoUri" class="modal-icon" />
            <h4 class="text">{{ wizardTitle }}</h4>
          </div>
          <BaseButton
            v-if="wizardPhase !== 'creating'"
            kind="auxiliary"
            class="close-icon"
            @click="wizardOpen = false"
          >
            <template #icon><CloseIcon /></template>
          </BaseButton>
        </div>

        <div class="body">
          <!-- Connecting: OAuth in progress -->
          <div v-if="wizardPhase === 'connecting'" class="workflow-status">
            <Spinner />
            <span
              >Waiting for {{ config?.label ?? app.name }} authorization…</span
            >
          </div>

          <!-- Configure: choose which items surface in Routine -->
          <div
            v-else-if="wizardPhase === 'configure' && config"
            class="workflow-configure"
          >
            <p class="workflow-configure-description">
              {{
                config.configureQuestion ??
                "Which tasks should appear in Routine?"
              }}
            </p>
            <div v-if="!config.requiresFilter" class="workflow-options">
              <button
                class="workflow-option"
                :class="{ selected: choice === 'optionA' }"
                @click="onFilterOptionAClick"
              >
                <span class="workflow-option-icon">{{
                  config.filterOptionA.icon
                }}</span>
                <span class="workflow-option-label">{{
                  config.filterOptionA.label
                }}</span>
              </button>
              <button
                v-if="config.filterOptionB"
                class="workflow-option"
                :class="{ selected: choice === 'optionB' }"
                @click="onFilterOptionBClick"
              >
                <span class="workflow-option-icon">{{
                  config.filterOptionB.icon
                }}</span>
                <span class="workflow-option-label">{{
                  config.filterOptionB.label
                }}</span>
              </button>
              <button
                v-if="config.filterOptionC"
                class="workflow-option"
                :class="{ selected: choice === 'optionC' }"
                @click="onFilterOptionCClick"
              >
                <span class="workflow-option-icon">{{
                  config.filterOptionC.icon
                }}</span>
                <span class="workflow-option-label">{{
                  config.filterOptionC.label
                }}</span>
              </button>
            </div>
            <div
              v-if="
                config.requiresFilter ||
                (choice === 'optionA' && config.fetchFilterItemsA) ||
                (choice === 'optionB' && config.filterOptionB) ||
                (choice === 'optionC' && config.filterOptionC)
              "
              class="workflow-filter-picker"
            >
              <BaseInputSelect
                select-only
                :options="filterItemOptions"
                :value="filterId"
                :placeholder="filterPickerPlaceholder"
                @update:value="filterId = $event"
              >
                <template v-if="filterItemsLoading" #right-icon>
                  <Spinner />
                </template>
              </BaseInputSelect>
            </div>
            <BaseAlert v-if="filterItemsError" size="small" state="error">
              {{ filterItemsError }}
            </BaseAlert>
            <BaseAlert v-if="duplicateSync" size="small" state="warning">
              This task sync is already set up for this account.
            </BaseAlert>
          </div>

          <TaskSyncMappingStep
            v-else-if="
              wizardPhase === 'mapping' && config && taskSyncMapping !== null
            "
            v-model="taskSyncMapping"
            :properties="mappingProperties"
            :loading="mappingLoading"
          />

          <!-- Creating: data source provisioning -->
          <div
            v-else-if="wizardPhase === 'creating' && config"
            class="workflow-status"
          >
            <Spinner />
            <span>Setting up your {{ config.label }} sync…</span>
          </div>

          <!-- Failed: the data source errored out, or is taking too long -->
          <div
            v-else-if="wizardPhase === 'failed' && config"
            class="workflow-status-failed"
          >
            <BaseAlert
              :state="wizardFailure?.kind === 'timeout' ? 'warning' : 'error'"
            >
              {{ wizardFailureMessage }}
            </BaseAlert>
          </div>

          <!-- Success: all done -->
          <div
            v-else-if="wizardPhase === 'success' && config"
            class="workflow-success"
          >
            <BaseAlert state="success">
              Your {{ config.label }} task sync is set up successfully.
            </BaseAlert>
            <component
              :is="config.IllustrationComponent"
              :choice="choice"
              :filter-name="successFilterName"
              :integration-label="config.label"
              :rows="config.illustrationRows ?? []"
            />
          </div>
        </div>

        <div v-if="wizardPhase === 'configure'" class="footer">
          <div class="buttons">
            <div class="buttons-left" />
            <div class="buttons-right">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Cancel"
                @click="wizardOpen = false"
              />
              <BaseButton
                kind="main"
                type="primary"
                text="Set up"
                :state="canSubmit ? 'default' : 'disabled'"
                @click="continueConfig"
              />
            </div>
          </div>
        </div>

        <div v-if="wizardPhase === 'mapping'" class="footer">
          <div class="buttons">
            <div class="buttons-left">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Back"
                @click="wizardPhase = 'configure'"
              />
            </div>
            <div class="buttons-right">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Cancel"
                @click="wizardOpen = false"
              />
              <BaseButton
                kind="main"
                type="primary"
                text="Set up"
                :state="canSubmitMapping ? 'default' : 'disabled'"
                @click="submitConfig"
              />
            </div>
          </div>
        </div>

        <div v-if="wizardPhase === 'failed'" class="footer">
          <div class="buttons">
            <div class="buttons-left" />
            <div class="buttons-right">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Close"
                @click="wizardOpen = false"
              />
              <!-- A timeout is still in flight: re-submitting the same
                   parameters would target the same data collection. -->
              <BaseButton
                v-if="wizardFailure?.kind === 'error'"
                kind="main"
                type="primary"
                text="Try again"
                @click="retryFailedSetup"
              />
            </div>
          </div>
        </div>

        <div v-if="wizardPhase === 'success'" class="footer">
          <div class="buttons">
            <div class="buttons-left" />
            <div class="buttons-right">
              <BaseButton
                kind="main"
                type="primary"
                text="Got it"
                @click="closeSuccess"
              />
            </div>
          </div>
        </div>
      </div>
    </VueFinalModal>

    <!-- Add data source modal -->
    <VueFinalModal
      v-model="addDataCollectionOpen"
      :focus-trap="modalFocusTrap"
      class="final-modal"
      :class="{
        'theme--default': theme === 'light',
        'theme--dark': theme === 'dark',
      }"
      overlay-transition="vfm-fade"
    >
      <div class="content add-data-collection-content">
        <div class="header">
          <div class="title">
            <img :src="app.logoUri" class="modal-icon" />
            <h4 class="text">Add Data Source</h4>
          </div>
          <BaseButton
            kind="auxiliary"
            class="close-icon"
            @click="addDataCollectionOpen = false"
          >
            <template #icon><CloseIcon /></template>
          </BaseButton>
        </div>
        <div class="body add-data-collection-body">
          <MembraneAddDataCollection
            :key="addDataCollectionKey"
            :org-id="orgId"
            :app="app"
            :modal="true"
            :on-done="
              () => {
                addDataCollectionOpen = false;
              }
            "
            :on-validity-change="
              (v: boolean) => {
                addDataCollectionFormValid = v;
              }
            "
            :on-submit-ready="
              (fn: () => Promise<void>) => {
                addDataCollectionSubmit = fn;
              }
            "
          />
        </div>
        <div class="footer">
          <div class="buttons">
            <div class="buttons-left" />
            <div class="buttons-right">
              <BaseButton
                kind="main"
                type="tertiary"
                text="Cancel"
                @click="addDataCollectionOpen = false"
              />
              <BaseButton
                kind="main"
                type="primary"
                text="Confirm"
                :state="addDataCollectionFormValid ? 'default' : 'disabled'"
                @click="addDataCollectionSubmit?.()"
              />
            </div>
          </div>
        </div>
      </div>
    </VueFinalModal>
  </div>
</template>

<script lang="ts">
import {
  Connection,
  Integration,
  WorkspaceElementState,
} from "@membranehq/sdk";
import { computed, defineComponent, ref } from "vue";
import type { PropType, Ref } from "vue";
import { VueFinalModal } from "vue-final-modal";
import { useRoute, useRouter } from "vue-router";

import type {
  GraphDatabase,
  GraphDatabaseDictionaryDictionary,
  TaskBinding,
  TaskBindingDictionary,
} from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect from "@/components/base/BaseInputSelect.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import WarningIcon from "@/components/icons/WarningIcon.vue";
import * as inject from "@/components/inject";
import { modalFocusTrap } from "@/components/modals/focus-trap";
import MembraneAddDataCollection from "@/components/settings/MembraneAddDataCollection.vue";
import type { StatusValueOption } from "@/components/settings/status-value-mapping";
import TaskBindingEditor, {
  type TaskBindingPanel,
} from "@/components/settings/TaskBindingEditor.vue";
import Spinner from "@/components/Spinner.vue";
import { getOrganization } from "@/composables/useProfile";
import { useReleaser } from "@/ref";
import { isRecord } from "@/utils/unknown";

import { connectionVersionAction } from "./connectionVersionUpdate";
import ConnectionVersionUpdate from "./ConnectionVersionUpdate.vue";
import {
  lastSyncedText,
  latestDataCollectionLastSynced,
} from "./dataCollection";
import IntegrationDataSourcesSection from "./IntegrationDataSourcesSection.vue";
import TaskSyncMappingStep from "./TaskSyncMappingStep.vue";
import type {
  IntegrationSyncConfig,
  TaskSyncEntry,
} from "./useIntegrationSyncWorkflow";
import {
  getStringCellParam,
  providerStatusOptions,
  useIntegrationSyncWorkflow,
  useNoTaskSyncWorkflow,
} from "./useIntegrationSyncWorkflow";
import { useMembraneSetup } from "./useMembraneSetup";

const ENTITLEMENT_KEY = "connections";

export function taskBindingForSync(
  sync: TaskSyncEntry,
  bindings: TaskBindingDictionary
): { id: string; binding: TaskBinding } | null {
  return taskBindingForTable(bindings, sync.workspaceId, sync.tableId);
}

export function taskBindingForTable(
  bindings: TaskBindingDictionary,
  workspaceId: string,
  tableId: string | null
): { id: string; binding: TaskBinding } | null {
  if (!tableId) return null;
  const entry = Object.entries(bindings).find(
    ([, binding]) =>
      binding.workspace_id === workspaceId && binding.table_id === tableId
  );
  return entry ? { id: entry[0], binding: entry[1] } : null;
}

export function canCustomizeTaskSync(
  sync: TaskSyncEntry,
  bindings: TaskBindingDictionary,
  databases: GraphDatabaseDictionaryDictionary | undefined
): boolean {
  return (
    sync.status === "ready" &&
    taskBindingForSync(sync, bindings) !== null &&
    !!(sync.tableId && databases?.[sync.workspaceId]?.[sync.tableId])
  );
}

export function needsTaskBindingRepair(
  sync: Pick<TaskSyncEntry, "status" | "workspaceId" | "tableId">,
  bindings: TaskBindingDictionary
): boolean {
  return (
    sync.status === "ready" &&
    sync.tableId !== null &&
    taskBindingForTable(bindings, sync.workspaceId, sync.tableId) === null
  );
}

export default defineComponent({
  components: {
    AddIcon,
    BaseAlert,
    BaseButton,
    BaseInputSelect,
    ButtonMenu,
    CloseIcon,
    ConnectionVersionUpdate,
    CopyIcon,
    DatabaseIcon,
    DeleteIcon,
    EditIcon,
    InfoIcon,
    IntegrationDataSourcesSection,
    MembraneAddDataCollection,
    MoreIcon,
    RefreshIcon,
    Spinner,
    TaskIcon,
    TaskBindingEditor,
    TaskSyncMappingStep,
    UpgradeButton,
    VueFinalModal,
    WarningIcon,
  },
  props: {
    orgId: { type: String, required: true },
    app: { type: Object as PropType<Integration>, required: true },
    config: {
      type: Object as PropType<IntegrationSyncConfig | null>,
      required: false,
      default: null,
    },
  },
  async setup(props) {
    // All inject() calls must happen before any await.
    const controller = inject.controller();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const theme = inject.theme();
    const billing = inject.billing();
    const router = useRouter();
    const route = useRoute();
    const integrations = inject.integrations();
    const organizationId = computed(() => props.orgId);
    const targetWorkspaceId = computed(() => {
      const workspaces =
        getOrganization(profile.value, organizationId.value)?.workspaces ?? [];
      return (
        workspaces.find((workspace) => workspace.default)?.id ??
        workspaces[0]?.id
      );
    });

    const app: Ref<Integration> = computed(() => props.app);
    // The organization-wide view, for the org-level connection count below.
    const organizationIntegrations =
      integrations.forOrganization(organizationId);

    const releaser = useReleaser();
    const setup = await releaser.add(
      useMembraneSetup(
        organizationId,
        controller,
        integrations,
        app,
        router,
        route
      )
    );
    const taskBindings = await releaser.add(controller.task.binding.all());

    const {
      connections,
      enabledDataCollections,
      dataSourcesSupported,
      reconnectConnection,
      updateConnection,
      isUpdatingConnection,
      deleteSource,
      forceRefreshSource,
      openDatabase,
      copyIntegrationId,
      getConnectionDetail,
      getSourceKey,
      getDataCollectionStatus,
      getDataCollectionStatusText,
      getSubStatusIndicator,
      getDatabaseId,
    } = setup;

    const addDataCollectionOpen = ref(false);
    const addDataCollectionKey = ref(0);
    const addDataCollectionFormValid = ref(false);
    const addDataCollectionSubmit = ref<(() => Promise<void>) | null>(null);
    const openAddDataCollection = () => {
      addDataCollectionKey.value += 1;
      addDataCollectionFormValid.value = false;
      addDataCollectionSubmit.value = null;
      addDataCollectionOpen.value = true;
    };
    const customizeOpen = ref(false);
    const customizeSyncEntry = ref<TaskSyncEntry | null>(null);
    const customizeBinding = ref<{
      id: string;
      binding: TaskBinding;
    } | null>(null);
    const customizeDatabase = ref<GraphDatabase | null>(null);
    const customizeStatusOptions = ref<StatusValueOption[]>([]);
    const customizeStatusColumnLabel = ref("");
    const customizeScheduledColumnLabel = ref("");
    // A synced table's filter and URL are owned by the integration, so only the
    // task mappings are worth offering here.
    const customizeSections: TaskBindingPanel[] = ["status", "scheduled"];

    const bindingForSync = (sync: TaskSyncEntry) => {
      return taskBindingForSync(sync, taskBindings.value ?? {});
    };

    const canCustomizeSync = (sync: TaskSyncEntry) =>
      canCustomizeTaskSync(
        sync,
        taskBindings.value ?? {},
        workspaceDatabases.value
      );

    const customizeSync = async (sync: TaskSyncEntry) => {
      const binding = bindingForSync(sync);
      const database = sync.tableId
        ? workspaceDatabases.value?.[sync.workspaceId]?.[sync.tableId]
        : undefined;
      if (!binding || !database) return;
      customizeStatusOptions.value = [];
      customizeStatusColumnLabel.value = "";
      customizeScheduledColumnLabel.value = "";
      customizeSyncEntry.value = sync;
      customizeBinding.value = binding;
      customizeDatabase.value = database;
      customizeOpen.value = true;
      const taskMapping = props.config?.taskMapping;
      if (taskMapping) {
        const parameters = isRecord(sync.dataCollectionId.parameters)
          ? sync.dataCollectionId.parameters
          : {};
        const filterId =
          props.config?.getChoiceFromParameters?.(parameters).filterId ?? "";
        if (filterId) {
          try {
            const properties = await taskMapping.fetchProperties(
              sync.dataCollectionId.connection_id,
              filterId,
              setup.membrane.value
            );
            const defaults = taskMapping.defaultMapping(properties);
            const selectedId =
              getStringCellParam(parameters, "status_property_id") ||
              defaults.statusPropertyId;
            const selectedDateId =
              getStringCellParam(parameters, "date_property_id") ||
              defaults.datePropertyId;
            const selectedProperty = properties.find(
              (property) => property.id === selectedId
            );
            customizeStatusOptions.value =
              providerStatusOptions(selectedProperty);
            customizeStatusColumnLabel.value = selectedProperty?.name ?? "";
            customizeScheduledColumnLabel.value =
              properties.find((property) => property.id === selectedDateId)
                ?.name ?? "";
          } catch {
            // The generic binding editor remains usable if provider metadata
            // cannot be refreshed.
          }
        }
      }
    };

    const workflow = props.config
      ? useIntegrationSyncWorkflow(
          props.config,
          setup,
          controller,
          workspaceDatabases,
          router,
          route,
          targetWorkspaceId
        )
      : useNoTaskSyncWorkflow(setup, router, route);
    releaser.defer(workflow.release);

    const needsTaskBindingRepairFor = (sync: TaskSyncEntry) =>
      needsTaskBindingRepair(sync, taskBindings.value ?? {});

    const isRepairingTaskBinding = (sync: TaskSyncEntry) =>
      sync.tableId !== null &&
      workflow.repairingTaskBindings.value.has(
        `${sync.workspaceId}:${sync.tableId}`
      );

    /**
     * "Reset" in the customize modal: re-applies the integration's default
     * mappings to the binding, then re-derives the modal's state so it shows
     * the reset values.
     */
    const resetCustomizedBinding = async () => {
      const sync = customizeSyncEntry.value;
      if (!sync) return;
      await workflow.repairTaskBinding(sync, true);
      if (customizeOpen.value) await customizeSync(sync);
    };

    /**
     * Placeholder for the filter picker, named after whichever option card is
     * selected ("Select a specific project…"). Kept out of the template
     * because there are now three cards to choose between.
     */
    const filterPickerPlaceholder = computed(() => {
      const config = props.config;
      if (!config) return "";
      const label =
        workflow.choice.value === "optionC"
          ? config.filterOptionC?.label
          : workflow.choice.value === "optionB"
            ? config.filterOptionB?.label
            : config.filterOptionA.label;
      return `Select a ${(label ?? "item").toLowerCase()}…`;
    });

    // All collections of an account, task syncs included (used for health).
    const dataSourcesForConn = (connId: string) =>
      enabledDataCollections.value.filter(
        (e) => e.dataCollectionId.connection_id === connId
      );

    /** Task syncs configured for an account, in display form. */
    const taskSyncsFor = (connId: string) =>
      workflow.taskSyncsByConnection.value[connId] ?? [];

    const hasCompletionSelect = (sync: TaskSyncEntry) =>
      sync.status === "ready" &&
      sync.tableId !== null &&
      (workflow.completionActionOptionsByTable.value[sync.tableId]?.length ??
        0) > 1;

    const completionOptionsFor = (sync: TaskSyncEntry) => {
      const tableId = sync.tableId;
      return tableId
        ? (workflow.completionActionOptionsByTable.value[tableId] ?? [])
        : [];
    };

    const completionValueFor = (sync: TaskSyncEntry) => {
      const tableId = sync.tableId;
      return String(
        tableId ? (workflow.completionActionByTable.value[tableId] ?? 0) : 0
      );
    };

    /**
     * Data sources of an account in display form — every enabled collection
     * except the ones surfaced as task syncs above.
     */
    const dataSourcesFor = (connId: string) =>
      enabledDataCollections.value
        .filter(
          (e) =>
            e.dataCollectionId.connection_id === connId &&
            (!props.config ||
              e.dataCollectionId.data_collection_key !==
                props.config.collectionKey)
        )
        .map((e) => ({
          key: getSourceKey(e.dataCollectionId),
          workspaceId: e.workspaceId,
          dataCollectionId: e.dataCollectionId,
          dataCollection: e.dataCollection,
          databaseName: e.databaseName,
          status: getDataCollectionStatus(e.dataCollection),
          statusText: getDataCollectionStatusText(e.dataCollection),
          subStatus: getSubStatusIndicator(e.dataCollection),
          tableId: getDatabaseId(e.dataCollection),
        }));

    const openSourceDatabase = (
      item: { workspaceId: string; tableId: string | null },
      event: Event
    ) => {
      if (item.tableId !== null) {
        void openDatabase(item.workspaceId, item.tableId, event);
      }
    };

    const connectionsEntitlement = computed(() => {
      return (
        billing.value?.getEntitlement(ENTITLEMENT_KEY, organizationId.value)
          ?.value ?? undefined
      );
    });
    const numberOfConnectionsEntitledTo = computed(() => {
      if (connectionsEntitlement.value === undefined) return undefined;
      if (connectionsEntitlement.value.value === "∞")
        return Number.MAX_SAFE_INTEGER;
      return Number(connectionsEntitlement.value.value) || 0;
    });
    // Count connections org-wide (all integrations), for the entitlement gate.
    const numberOfConnections = computed(
      () => organizationIntegrations.connections.value.length
    );
    const canAddConnection = computed(() => {
      if (numberOfConnectionsEntitledTo.value === undefined) return false;
      return numberOfConnections.value < numberOfConnectionsEntitledTo.value;
    });

    /**
     * Update is only for a ready account on an older version of this
     * integration's connector. An account that is not ready keeps Reconnect.
     */
    const connectionVersionActionFor = (conn: Connection) =>
      connectionVersionAction({
        state: conn.state,
        connection: conn,
        integration: app.value,
      });

    /**
     * Health of a connected account: the account itself must be healthy and
     * none of its data collections may be in error.
     */
    const getConnectionHealth = (
      conn: Connection
    ): { healthy: boolean; tooltip: string } => {
      const label = props.config?.label ?? app.value.name;
      const accountHealthy =
        (conn.state ?? WorkspaceElementState.READY) ===
        WorkspaceElementState.READY;
      const sources = dataSourcesForConn(conn.id);
      const hasSourceError = sources.some(
        (e) => e.dataCollection.status === "error"
      );
      if (!accountHealthy) {
        return {
          healthy: false,
          tooltip: `${label} account is unhealthy. Try to reconnect it.`,
        };
      }
      if (hasSourceError) {
        return {
          healthy: false,
          tooltip: `${label} account is connected, but a data source has an error.`,
        };
      }
      if (sources.length === 0) {
        return {
          healthy: true,
          tooltip: `${label} account is connected.`,
        };
      }
      const synced = lastSyncedText(
        latestDataCollectionLastSynced(sources.map((e) => e.dataCollection))
      );
      const suffix = synced ? ` ${synced}.` : "";
      return {
        healthy: true,
        tooltip: `${label} account is connected and data is synchronizing.${suffix}`,
      };
    };

    return {
      modalFocusTrap,
      canAddConnection,
      connections,
      ENTITLEMENT_KEY,
      enabledDataCollections,
      dataSourcesSupported,
      reconnectConnection,
      updateConnection,
      isUpdatingConnection,
      connectionVersionActionFor,
      deleteSource,
      forceRefreshSource,
      openAddDataCollection,
      customizeOpen,
      customizeSyncEntry,
      customizeBinding,
      customizeDatabase,
      customizeStatusOptions,
      customizeStatusColumnLabel,
      customizeScheduledColumnLabel,
      customizeSections,
      canCustomizeSync,
      customizeSync,
      resetCustomizedBinding,
      needsTaskBindingRepair: needsTaskBindingRepairFor,
      isRepairingTaskBinding,
      addDataCollectionOpen,
      addDataCollectionKey,
      addDataCollectionFormValid,
      addDataCollectionSubmit,
      openDatabase,
      copyIntegrationId,
      getConnectionDetail,
      getConnectionHealth,
      taskSyncsFor,
      hasCompletionSelect,
      completionOptionsFor,
      completionValueFor,
      dataSourcesFor,
      openSourceDatabase,
      theme,
      filterPickerPlaceholder,
      ...workflow,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.app-description-hint {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-inline-start: 4px;
  cursor: default;
  @include themed() {
    color: t($grey-40);
  }

  :deep(svg) {
    width: 13px;
    height: 13px;
  }
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  opacity: 0;
  @include transition(opacity 0.2s ease-out);
  @include themed() {
    color: t($grey-40);
  }

  svg {
    width: 12px;
    height: 12px;
  }
}

.title-name-row:hover .copy-btn {
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
}

.context-action {
  width: 100%;
}

// ── Wizard ─────────────────────────────────────────────────────────────────

.workflow-empty {
  padding: 8px 0;
}

.workflow-empty-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  padding: 40px 32px;
  margin-top: 8px;
  border-radius: $border-radius-l;
  @include themed() {
    border: 1px solid t($grey-15);
    background-color: t($grey-03);
  }
}

.workflow-empty-logo-wrap {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  @include themed() {
    background-color: t($grey-05);
    box-shadow: 0 0 0 8px t($grey-03);
  }
}

.workflow-empty-logo {
  width: 32px;
  height: 32px;
  border-radius: $border-radius-m;
}

.workflow-empty-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.workflow-empty-headline {
  margin: 0;
  @extend %font-body-medium;
  @include themed() {
    color: t($primary);
  }
}

.workflow-empty-sub {
  margin: 0;
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.workflow-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  padding-top: 32px;

  span {
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }
}

.workflow-wizard-content {
  width: 480px !important;
  min-width: 0 !important;

  .modal-icon {
    height: 24px;
    width: 24px;
    border-radius: $border-radius-m;
  }

  > .body {
    display: flex;
    flex-direction: column;
    min-height: 180px;
  }
}

.add-data-collection-content {
  width: 520px !important;
  min-width: 0 !important;

  .modal-icon {
    height: 24px;
    width: 24px;
    border-radius: $border-radius-m;
  }
}

.add-data-collection-body {
  overflow-y: auto;
  max-height: calc(80vh - 80px);
}

// ── Configure ──────────────────────────────────────────────────────────────

.workflow-configure {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .workflow-configure-description {
    @extend %font-body-regular;
    margin: 0;
    @include themed() {
      color: t($grey-50);
    }
  }

  .workflow-options {
    display: flex;
    gap: 12px;
  }

  .workflow-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px;
    border-radius: $border-radius-m;
    border: 1px solid transparent;
    @include transition(border-color 0.15s ease, background-color 0.15s ease);

    @include themed() {
      background-color: t($grey-05);
      border-color: t($grey-10);
      color: t($primary);
    }

    &:hover {
      @include themed() {
        border-color: t($grey-30);
      }
    }
    &.selected {
      @include themed() {
        border-color: t(tertiary-100);
        background-color: t(tertiary-10);
      }
    }

    .workflow-option-icon {
      font-size: 18px;
      line-height: 1;
    }
    .workflow-option-label {
      @extend %font-body-medium;
    }
  }

  .workflow-filter-picker {
    max-width: 320px;
  }
}

// ── Success ────────────────────────────────────────────────────────────────

.workflow-success {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.workflow-status-failed {
  display: flex;
  flex-direction: column;
  flex: 1;
}

// ── Ready ──────────────────────────────────────────────────────────────────

.workflow-ready {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h3 {
    margin-bottom: 0;
    margin-top: 24px;
  }
}

.workflow-account {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: $border-radius-m;
  @include themed() {
    border: 1px solid t($grey-10);
  }
}

.workflow-ready-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.workflow-ready-icon {
  width: 18px;
  height: 18px;
  border-radius: $border-radius-s;
}

.workflow-ready-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: default;
  &.healthy {
    background-color: #10b981;
  }
  &.error {
    @include themed() {
      background-color: t($secondary-50);
    }
  }
}

.workflow-ready-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  .workflow-ready-name {
    @extend %font-body-regular;
  }
}

.workflow-ready-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.workflow-how-to-use {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;

  &:hover .workflow-how-to-use-icon :deep(.stroke) {
    @include themed() {
      stroke: t($primary);
    }
  }
}

.workflow-how-to-use-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;

  :deep(.stroke) {
    @include themed() {
      stroke: t($grey-40);
    }
  }
}

.workflow-task-sync {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.workflow-sync {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 8px;
}

.workflow-sync-row {
  display: flex;
  align-items: center;
  gap: 10px;

  &.clickable {
    border-radius: $border-radius-s;
    @include transition(background-color 0.15s ease-out);

    &:hover {
      @include themed() {
        background-color: t($grey-03);
      }
    }
  }
}

.workflow-sub-status-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: default;
  @include themed() {
    color: t($grey-50);
  }
}

.workflow-sync-text {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.workflow-sync-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.workflow-sync-name {
  @extend %font-body-medium;
}

.workflow-sync-binding-warning {
  @extend %font-caption-regular;
  @include themed() {
    color: t($tertiary);
  }
}

.workflow-sync-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.workflow-add-sync {
  display: flex;
  padding-top: 16px;
}

.workflow-account-setup {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.workflow-setup-card {
  flex-direction: column !important;
  align-items: center !important;
  text-align: center;
  gap: 16px;
  padding: 24px 16px !important;
  margin-bottom: 0 !important;
}

.workflow-setup-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workflow-add-source-link {
  background: none;
  border: none;
  padding: 0;
  @extend %font-caption-regular;
  @include transition(color 0.15s ease);
  @include themed() {
    color: t($grey-50);
  }

  &:hover {
    @include themed() {
      color: t($grey-80);
    }
  }
}

.workflow-sync-spinner {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.workflow-sync-completion-label {
  @extend %font-small-regular;
  cursor: help;
  @include themed() {
    color: t($grey-60);
  }
}

.workflow-completion-select {
  width: 140px;

  :deep(input) {
    text-overflow: ellipsis;
  }
}
</style>
